import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathRoundedSquareIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

interface RescheduleRequestApi {
  id?: string;
  _id?: string | { $oid?: string };
  selectionId: string;
  sessionDate: string;
  teacherName: string;
  studentName: string;
  subject: string;
  classMode: 'ONLINE' | 'OFFLINE' | 'HYBRID';
  frequency: 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'WEEKENDS';
  requestedByRole: 'STUDENT' | 'TEACHER';
  requestedByName: string;
  reason: string;
  currentScheduledAt: string;
  proposedScheduledAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewNote?: string;
}

const statusClasses: Record<RescheduleRequestApi['status'], string> = {
  PENDING: 'border-amber-200 bg-amber-50 text-amber-800',
  APPROVED: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  REJECTED: 'border-rose-200 bg-rose-50 text-rose-800',
};

const roleClasses: Record<RescheduleRequestApi['requestedByRole'], string> = {
  STUDENT: 'bg-sky-100 text-sky-800',
  TEACHER: 'bg-violet-100 text-violet-800',
};

const AdminScheduleDashboard: React.FC = () => {
  const [requests, setRequests] = useState<RescheduleRequestApi[]>([]);
  const [decisionReasons, setDecisionReasons] = useState<Record<string, string>>({});
  const [reviewingId, setReviewingId] = useState<string | null>(null);

  const resolveRequestId = (request: RescheduleRequestApi) => {
    if (request.id) return request.id;
    if (typeof request._id === 'string') return request._id;
    if (request._id && typeof request._id === 'object' && request._id.$oid) return request._id.$oid;
    return '';
  };

  const loadData = async () => {
    try {
      const response = await api.get<RescheduleRequestApi[]>('/admin/reschedule-requests');
      setRequests(response.data || []);
    } catch (error) {
      console.error('Failed to load reschedule requests:', error);
      setRequests([]);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const pendingRequests = useMemo(
    () => requests.filter((request) => request.status === 'PENDING'),
    [requests],
  );

  const reviewedRequests = useMemo(
    () => requests.filter((request) => request.status !== 'PENDING'),
    [requests],
  );

  const metrics = useMemo(
    () => [
      {
        label: 'Pending approvals',
        value: pendingRequests.length,
        hint: 'Requests waiting for admin action',
        icon: ClockIcon,
        accent: 'from-amber-500/20 via-amber-400/10 to-transparent',
      },
      {
        label: 'Approved today view',
        value: reviewedRequests.filter((request) => request.status === 'APPROVED').length,
        hint: 'Reschedules already cleared',
        icon: CheckCircleIcon,
        accent: 'from-emerald-500/20 via-emerald-400/10 to-transparent',
      },
      {
        label: 'Rejected cases',
        value: reviewedRequests.filter((request) => request.status === 'REJECTED').length,
        hint: 'Requests that need follow-up',
        icon: XCircleIcon,
        accent: 'from-rose-500/20 via-rose-400/10 to-transparent',
      },
      {
        label: 'Total volume',
        value: requests.length,
        hint: 'All reschedule events tracked',
        icon: ArrowPathRoundedSquareIcon,
        accent: 'from-sky-500/20 via-sky-400/10 to-transparent',
      },
    ],
    [pendingRequests.length, requests.length, reviewedRequests],
  );

  const handleReview = async (requestId: string, decision: 'APPROVED' | 'REJECTED') => {
    const reason = decisionReasons[requestId] || '';
    try {
      setReviewingId(requestId);
      await api.patch(`/admin/reschedule-requests/${requestId}/review`, {
        decision,
        note: reason.trim() || undefined,
      });
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to review request.';
      alert(message);
      return;
    } finally {
      setReviewingId(null);
    }

    alert(`Request ${decision.toLowerCase()} successfully.`);
    await loadData();
  };

  return (
    <div className="space-y-6 text-slate-900">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.2),_transparent_30%),linear-gradient(135deg,_#0f172a_0%,_#111827_42%,_#1d4ed8_100%)] p-6 text-white shadow-[0_30px_80px_-48px_rgba(15,23,42,0.85)] sm:p-8">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.85fr)]">
          <div>
            <span className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-sky-100">
              Scheduling control
            </span>
            <h1 className="mt-4 max-w-2xl text-3xl font-semibold tracking-tight sm:text-4xl">
              Clear the reschedule queue before it compounds into support work.
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200 sm:text-base">
              Review proposed schedule shifts, compare the old and new slots, and capture decision notes in one pass.
            </p>
          </div>
          <div className="grid gap-3 rounded-[1.75rem] border border-white/10 bg-white/10 p-4 backdrop-blur">
            <div className="rounded-[1.25rem] border border-white/10 bg-slate-950/30 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">Queue health</p>
              <p className="mt-3 text-3xl font-semibold">{pendingRequests.length}</p>
              <p className="mt-2 text-sm text-slate-300">Pending requests currently waiting for a decision.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-[1.25rem] border border-white/10 bg-white/10 p-4">
                <p className="text-xs text-slate-300">Approved</p>
                <p className="mt-2 text-2xl font-semibold">
                  {reviewedRequests.filter((request) => request.status === 'APPROVED').length}
                </p>
              </div>
              <div className="rounded-[1.25rem] border border-white/10 bg-white/10 p-4">
                <p className="text-xs text-slate-300">Rejected</p>
                <p className="mt-2 text-2xl font-semibold">
                  {reviewedRequests.filter((request) => request.status === 'REJECTED').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              key={metric.label}
              className={`relative overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]`}
            >
              <div className={`absolute inset-x-0 top-0 h-24 bg-gradient-to-br ${metric.accent}`} />
              <div className="relative flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{metric.label}</p>
                  <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">{metric.value}</p>
                  <p className="mt-2 text-sm text-slate-500">{metric.hint}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <Icon className="h-6 w-6 text-slate-700" />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(340px,0.95fr)]">
        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
          <div className="border-b border-slate-200 px-6 py-5">
            <h2 className="text-xl font-semibold tracking-tight text-slate-950">Pending request queue</h2>
            <p className="mt-1 text-sm text-slate-500">
              Every item below is actionable. Compare the current and proposed slot before confirming.
            </p>
          </div>

          <div className="divide-y divide-slate-200">
            {pendingRequests.length === 0 ? (
              <div className="px-6 py-14 text-center">
                <CalendarDaysIcon className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-4 text-base font-semibold text-slate-900">No pending reschedule requests</p>
                <p className="mt-2 text-sm text-slate-500">The approval queue is clear right now.</p>
              </div>
            ) : (
              pendingRequests.map((request) => {
                const requestId = resolveRequestId(request);
                if (!requestId) return null;

                return (
                  <article key={requestId} className="space-y-5 px-6 py-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusClasses[request.status]}`}>
                            {request.status}
                          </span>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${roleClasses[request.requestedByRole]}`}>
                            {request.requestedByRole}
                          </span>
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                            {request.classMode} / {request.frequency}
                          </span>
                        </div>
                        <h3 className="mt-4 text-xl font-semibold text-slate-950">{request.subject}</h3>
                        <p className="mt-1 text-sm text-slate-500">
                          Requested by {request.requestedByName} for {request.studentName} with {request.teacherName}.
                        </p>
                      </div>
                      <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        Session date: <span className="font-semibold text-slate-900">{request.sessionDate}</span>
                      </div>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Current slot</p>
                        <p className="mt-3 text-lg font-semibold text-slate-950">
                          {formatDateTime(request.currentScheduledAt)}
                        </p>
                      </div>
                      <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Proposed slot</p>
                        <p className="mt-3 text-lg font-semibold text-emerald-950">
                          {formatDateTime(request.proposedScheduledAt)}
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.9fr)]">
                      <div className="rounded-[1.5rem] border border-slate-200 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Reason submitted</p>
                        <p className="mt-3 text-sm leading-6 text-slate-700">{request.reason}</p>
                      </div>
                      <div className="rounded-[1.5rem] border border-slate-200 p-4">
                        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400" htmlFor={`note-${requestId}`}>
                          Admin note
                        </label>
                        <textarea
                          id={`note-${requestId}`}
                          value={decisionReasons[requestId] || ''}
                          onChange={(e) =>
                            setDecisionReasons((prev) => ({ ...prev, [requestId]: e.target.value }))
                          }
                          rows={4}
                          className="mt-3 w-full rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                          placeholder="Document why this change is approved or rejected"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                      <button
                        className="rounded-full border border-rose-200 bg-rose-50 px-5 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => handleReview(requestId, 'REJECTED')}
                        disabled={reviewingId === requestId}
                      >
                        Reject request
                      </button>
                      <button
                        className="rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => handleReview(requestId, 'APPROVED')}
                        disabled={reviewingId === requestId}
                      >
                        {reviewingId === requestId ? 'Saving decision...' : 'Approve change'}
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </div>

        <aside className="space-y-6">
          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 px-6 py-5">
              <h2 className="text-lg font-semibold tracking-tight text-slate-950">Review guidelines</h2>
            </div>
            <div className="space-y-4 px-6 py-5 text-sm text-slate-600">
              <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                Approve when the new slot preserves teacher availability and student continuity.
              </div>
              <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                Reject when the reason is vague, operationally risky, or creates repeated churn.
              </div>
              <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                Add a short admin note when the requester will need context after the decision.
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 px-6 py-5">
              <h2 className="text-lg font-semibold tracking-tight text-slate-950">Recent decisions</h2>
              <p className="mt-1 text-sm text-slate-500">Latest reviewed requests for quick audit context.</p>
            </div>
            <div className="divide-y divide-slate-200">
              {reviewedRequests.length === 0 ? (
                <div className="px-6 py-8 text-sm text-slate-500">No reviewed requests yet.</div>
              ) : (
                reviewedRequests.slice(0, 8).map((request) => (
                  <div key={resolveRequestId(request)} className="space-y-3 px-6 py-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-950">{request.subject}</p>
                      <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${statusClasses[request.status]}`}>
                        {request.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {request.teacherName} with {request.studentName}
                    </p>
                    <p className="text-sm text-slate-700">{formatDateTime(request.proposedScheduledAt)}</p>
                    {request.reviewNote ? (
                      <p className="rounded-2xl bg-slate-50 px-3 py-2 text-xs text-slate-600">{request.reviewNote}</p>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default AdminScheduleDashboard;
